import type { CommandHandler } from '../types';

/**
 * Parses the `{ "roles": "a,b,c" }` JSON stored in command.settings (addlogin command)
 */
function parseRolesFromSettings(settings: string | null): string[] {
  if (!settings) return [];
  try {
    const parsed = JSON.parse(settings) as { roles?: string };

    return (parsed?.roles ?? '')
      .split(',')
      .map((r) => r.trim())
      .filter(Boolean);
  } catch {
    return [];
  }
}

/**
 * Handler for the 'editRoles' custom command (alias: "editroles").
 * Available roles are fetched inside the dialog via GetUmbracoUserGroups;
 * here we only resolve the selection and the currently-assigned roles
 * (read from the RelationRoles field of the first selected row).
 */
export const editRolesCommandHandler: CommandHandler = (_command, context) => {
  const { selectedRows, metadata, displayData, gridData } = context;

  if (!selectedRows || selectedRows.size === 0) {
    return { success: false, error: 'Please select at least one row' };
  }

  const relationKeys = Array.from(selectedRows);
  const source = displayData?.length ? displayData : gridData;
  const firstRow = source?.find((row) => String(row.Id) === relationKeys[0]) ?? null;

  const currentRoles =
    firstRow && typeof firstRow.RelationRoles === 'string'
      ? firstRow.RelationRoles.split(',')
          .map((r) => r.trim())
          .filter(Boolean)
      : [];

  return {
    success: true,
    userManagementModal: {
      type: 'editRoles',
      nodeKey: metadata.contentKey,
      relationKeys,
      currentRoles,
    },
  };
};

/**
 * Handler for the 'addlogin' command.
 * Available roles come from command.settings, e.g. { "roles": "regular,supervisor,..." }
 */
export const addLoginCommandHandler: CommandHandler = (command, context) => {
  const { selectedRows, metadata } = context;

  if (!selectedRows || selectedRows.size === 0) {
    return { success: false, error: 'Please select at least one row' };
  }

  const availableRoles = parseRolesFromSettings(command.settings);

  return {
    success: true,
    userManagementModal: {
      type: 'addLogin',
      nodeKey: metadata.contentKey,
      relationKeys: Array.from(selectedRows),
      availableRoles,
      availableRolesWarning:
        availableRoles.length === 0
          ? 'Roles could not be loaded for this command. Please configure roles before creating a login.'
          : undefined,
    },
  };
};
